import { bytesToNDigits } from '@mail/cross-sizes-utils';
import React, { type MouseEvent } from 'react';
import { isFamilySubsInTariff, isOnlyWebAdInTariffs } from 'reactApp/appHelpers/featuresHelpers';
import { isFeatureAiLetterPromo } from 'reactApp/appHelpers/featuresHelpers/features/aiLetterStylePromo';
import { AUTOUPLOAD_SHORT_FEATURE_TEXT } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { mailSpaceBenefitsTouch, mailSpaceBenifits } from 'reactApp/appHelpers/featuresHelpers/features/mailSpaceBenefits';
import { isMinTariffsWithAds } from 'reactApp/appHelpers/featuresHelpers/features/minTariffsWithAds';
import { paidUploadLimit } from 'reactApp/appHelpers/featuresHelpers/features/paidUploadLimit';
import { isPriceDifferentiation } from 'reactApp/appHelpers/featuresHelpers/features/priceDifferentiation';
import {
    SHOW_EDITING_DOCUMENT_SHORT_FEATURE_TEXT,
    showEditingDocumentFeatureB,
    showEditingDocumentFeatureC,
} from 'reactApp/appHelpers/featuresHelpers/features/showEditingDocumentFeature';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { renderFeaturesListMobileModal } from 'reactApp/sections/MobileQuotaTariffsLanding/FeaturesModal/FeaturesModal.helpers';
import type { MobileBuyClickType } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard.types';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import type { Product } from 'reactApp/types/Billing';
import { getPeriodName, isMonthPeriod } from 'reactApp/utils/Period';
import { formatPrice, getMonthProductPrice } from 'reactApp/utils/priceHelpers';

export const getButtonText = (price: number, period: string, isFullText: boolean, product: Product, priceByMonth?: boolean): string => {
    if (product.hasTrial && product.trialPeriod) {
        return `${isMonthPeriod(product.trialPeriod) ? 'Месяц' : 'Год'} бесплатно`;
    }

    if (priceByMonth) {
        return `${formatPrice(getMonthProductPrice(product))} ₽ в месяц`;
    }

    if (isFullText) {
        return `Купить за ${formatPrice(price, 'ru')} ₽ в ${getPeriodName(period)}`;
    }

    return `${formatPrice(price, 'ru')} ₽ в ${getPeriodName(period)}`;
};

export const getYearPriceWithMonthProduct = (products) => {
    const monthProduct = products.find((p) => isMonthPeriod(p.period));

    return `${formatPrice((monthProduct?.price || 0) * 12, 'ru')} ₽ в год`;
};

export const getDiscountText = (discountPrice: number, discountPeriod: string): string => {
    return `${formatPrice(discountPrice, 'ru')} ₽/${getPeriodName(discountPeriod)}`;
};

export const getTariffLabelByQuota = (quota: number): string => {
    if (quota >= 256) {
        return 'Оптимальный';
    } else if (quota >= 128) {
        return 'Популярный';
    } else if (quota >= 64) {
        return 'Специальный';
    }

    return 'Выгодный';
};
// tempexp_17509-next-line
const litres2mFree = (
    <>
        Подписка на Литрес —<br />2 месяца бесплатно
    </>
);
const withoutAdInWeb = 'Без рекламы в веб-версии';
const withoutAdInCloudAndMail = 'Без рекламы в Почте и Облаке';
const uploadLimit = 'Загрузка файлов до 100 ГБ';
const uploadLimitPromoQuota = `Загрузка файлов до ${bytesToNDigits(paidUploadLimit, 3).value}`;
const shareSpace = 'Делитесь местом с близкими';
const sendLimit = 'Отправка файлов до 100 ГБ';
export const familyFoldersFeatureText = 'Семейные папки и альбомы';
export const familyMembersFeatureText = 'Добавление ещё 3 участников';
export const familyAutouploadFeatureText = 'Безлимит для фото с телефонов';
export const deletingDuplicates = 'Удаление дубликатов';
export const aiLetterSet = 'Генерация и стиль писем';

export const getFeatureList = (
    countSnapshotsBySpace?: number,
    isSingleQuota?: boolean,
    skipBenefitsFeature?: boolean,
    isRebrandingQuotaLanding?: boolean,
    isRebrandingSubscription?: boolean,
    // tempexp_17509-next-line
    isFromLitres?: boolean,
    // tempexp_17906-start
    yearProduct?: Product,
    onBuyClick?: MobileBuyClickType,
    autoUploadFeature?: boolean,
    // tempexp_17906-end
    isFamily?: boolean,
    isDeletingDuplicates?: boolean
) => {
    /* tempexp_17509-start */
    if (isFromLitres) {
        return [
            litres2mFree,
            isOnlyWebAdInTariffs ? withoutAdInWeb : withoutAdInCloudAndMail,
            isFamilySubsInTariff ? (isRebrandingQuotaLanding ? familyMembersFeatureText : shareSpace) : 'Быстрый ответ поддержки',
            !skipBenefitsFeature && (
                <>
                    И другие{'\u00A0'}
                    <a href="#benefits">возможности</a>
                </>
            ),
        ].filter((val) => !!val);
    }
    /* tempexp_17509-end */
    if (isSingleQuota) {
        // tempexp_17906-start
        const scrollToBenifits = (e: MouseEvent) => {
            e.preventDefault();
            sendQuotaGa({ action: 'tariff_benefits', label: 'click' });
            document.querySelector('#benefits')?.scrollIntoView({ behavior: 'smooth' });
        };

        const handleShowBenifits = (e: MouseEvent) => {
            e.preventDefault();
            renderFeaturesListMobileModal({
                product: yearProduct,
                onBuyClick,
            });
        };
        // tempexp_17906-end

        // tempexp_17906-next-line
        const lastFeature = (mailSpaceBenifits === 'c' || (mailSpaceBenefitsTouch && isFamily)) && yearProduct && (
            <>
                И другие{'\u00A0'}
                <a href="#benefits" onClick={handleShowBenifits}>
                    возможности
                </a>
            </>
        );

        let familyFeature;
        if (!autoUploadFeature) {
            familyFeature = isFamilySubsInTariff
                ? isRebrandingQuotaLanding
                    ? familyMembersFeatureText
                    : shareSpace
                : 'Быстрый ответ поддержки';
        }

        if (isFamily) {
            return [
                familyAutouploadFeatureText,
                familyMembersFeatureText,
                familyFoldersFeatureText,
                deletingDuplicates,
                withoutAdInCloudAndMail,
                lastFeature,
            ];
        }

        // tempexp_19902-start

        let ads;

        if (
            !(isPriceDifferentiation || isMinTariffsWithAds) ||
            (isMinTariffsWithAds && yearProduct?.space?.original && yearProduct?.space?.original >= SPACE_LIST.gb_256) ||
            (isPriceDifferentiation && yearProduct?.space?.original && yearProduct?.space?.original >= SPACE_LIST.gb_256)
        ) {
            ads = isOnlyWebAdInTariffs ? withoutAdInWeb : withoutAdInCloudAndMail;
        }
        // tempexp_19902-end

        return [
            showEditingDocumentFeatureB || showEditingDocumentFeatureC ? SHOW_EDITING_DOCUMENT_SHORT_FEATURE_TEXT : '',
            autoUploadFeature ? AUTOUPLOAD_SHORT_FEATURE_TEXT : undefined,
            isDeletingDuplicates ? (isFeatureAiLetterPromo ? aiLetterSet : deletingDuplicates) : undefined,
            ads,
            uploadLimitPromoQuota,
            familyFeature,
            // tempexp_17906-next-line
            (!skipBenefitsFeature || mailSpaceBenifits === 'b') && (
                <>
                    И другие{'\u00A0'}
                    <a href="#benefits" onClick={mailSpaceBenifits === 'b' ? scrollToBenifits : undefined}>
                        возможности
                    </a>
                </>
            ),
            lastFeature,
        ].filter((val) => !!val);
    }

    if (isRebrandingSubscription) {
        const ads =
            yearProduct &&
            yearProduct?.space?.original >= SPACE_LIST.gb_256 &&
            (isOnlyWebAdInTariffs ? withoutAdInWeb : withoutAdInCloudAndMail);
        return [...(ads ? [ads] : []), uploadLimit, sendLimit, ...(isFamilySubsInTariff ? ['Делитесь местом с близкими'] : [])];
    }

    return [
        isFamilySubsInTariff ? shareSpace : countSnapshotsBySpace && `+${countSnapshotsBySpace} новых фото в Облаке`,
        uploadLimit,
        sendLimit,
        isOnlyWebAdInTariffs ? withoutAdInWeb : withoutAdInCloudAndMail,
    ].filter((val) => !!val);
};
